import { userLogin } from "api/users";
import { get2FTAuthVerify } from "api/graphQL_users";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { setToken } from "utils/auth";
import Recover from "views/admin/profile/components/Recover";
import TwoFactorAuthentication from "views/admin/profile/components/TwoFactorAuthenticationLogin";

export default function SignIn() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [model2FA, setModel2FA] = useState<any>("");
  const [errEmail, setErrEmail] = useState("");
  const [errPassword, setErrPassword] = useState("");
  const [data, setData] = useState<any>();
  const [loginData, getLoginData] = useState<any>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errAuthCode, setErrAuthCode] = useState("");
  const [status, setStatus] = useState("Login");
  const [passwordShow, setPasswordShow] = useState(false);
  const [authCode, setAuthCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(new Array(6).fill(null));

  useEffect(() => {
    if (status === "OTP" && inputRefs.current[0]) inputRefs.current[0]?.focus();
  }, [status]);

  function handleEmailChange(e: any) { setEmail(e.target.value); setErrEmail(""); }
  function handlePasswordChange(e: any) { setPassword(e.target.value); setErrPassword(""); }

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d$/.test(value) || value === "") {
      const newCode = [...authCode];
      newCode[index] = value;
      setAuthCode(newCode);
      if (value !== "" && index < 5 && inputRefs.current[index + 1]) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && authCode[index] === "" && index > 0) inputRefs.current[index - 1]?.focus();
  };

  function validateData() {
    if (!email) { setErrEmail("Please enter your email"); return false; }
    if (!password) { setErrPassword("Please enter your password"); return false; }
    if (password.length < 8) { setErrPassword("Password must be at least 8 characters"); return false; }
    return true;
  }

  function validateDataOtp() {
    if (!authCode) { setErrAuthCode("Please enter your OTP"); return false; }
    if (authCode.length < 6) { setErrAuthCode("OTP must be 6 digits"); return false; }
    return true;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    try {
      setIsLoading(true);
      await userLogin({ email, password })
        .then((data) => {
          if (data?.auth_2fa) { getLoginData(data); setStatus("OTP"); }
          else { setToken(data.auth_token); navigate("/admin/dashboard"); }
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? "Please provide valid values"))
        .finally(() => setIsLoading(false));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Login failed");
      setIsLoading(false);
    }
  }

  const handleVerify = async (code: string) => {
    if (!validateDataOtp()) return;
    try {
      setIsLoading(true);
      await get2FTAuthVerify({ otp: code, user_id: loginData?.user_id, secret_key: loginData?.secret_key, inside_flag: true })
        .then((data) => {
          if (data.status === "false") toast.error(data.message);
          else { setToken(data.auth_token); toast.success("Login successfully"); navigate("/admin/dashboard"); }
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? "Please provide valid values"))
        .finally(() => setIsLoading(false));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Login failed");
      setIsLoading(false);
    }
  };

  const handleSubmit2FA = (e: React.FormEvent) => { e.preventDefault(); handleVerify(authCode.join("")); };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) { setAuthCode(pasted.split("")); inputRefs.current[5]?.focus(); }
  };

  return (
    <div className="si-outer">
      {status === "Login" ? (
        <div className="si-box" key="login">

          {/* ── Header ── */}
          <div className="si-hd">
            <div className="si-hd-badge">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Secure Login
            </div>
            <h1 className="si-title">Sign in to your account</h1>
            <p className="si-sub">Access your payment dashboard and manage your transactions.</p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="si-form" noValidate>

            <div className="si-field">
              <label className="si-lbl" htmlFor="email">Email address</label>
              <div className="si-inp-wrap">
                <span className="si-inp-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  className={`si-inp${errEmail ? " si-inp--err" : ""}`}
                />
              </div>
              {errEmail && (
                <p className="si-err-msg">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {errEmail}
                </p>
              )}
            </div>

            <div className="si-field">
              <div className="si-lbl-row">
                <label className="si-lbl" htmlFor="password">Password</label>
              </div>
              <div className="si-inp-wrap">
                <span className="si-inp-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  id="password"
                  type={passwordShow ? "text" : "password"}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  className={`si-inp${errPassword ? " si-inp--err" : ""}`}
                />
                <button type="button" className="si-eye-btn" onClick={() => setPasswordShow(!passwordShow)} aria-label="Toggle password visibility">
                  {passwordShow ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>
                      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>
                      <line x1="2" y1="2" x2="22" y2="22"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {errPassword && (
                <p className="si-err-msg">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {errPassword}
                </p>
              )}
            </div>

            <button type="submit" className="si-btn" disabled={isLoading}>
              {isLoading ? (
                <span className="si-spinner" />
              ) : (
                <>
                  <span>Sign in</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* ── Security note ── */}
          <div className="si-security-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Protected by 256-bit SSL encryption</span>
          </div>
        </div>
      ) : (
        /* ── 2FA ── */
        <div className="si-box" key="otp">
          <div className="si-hd">
            <div className="si-hd-badge si-hd-badge--green">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Two-Factor Authentication
            </div>
            <h1 className="si-title">Verify your identity</h1>
            <p className="si-sub">Open your authenticator app and enter the 6-digit code to continue.</p>
          </div>

          <form onSubmit={handleSubmit2FA} className="si-form">
            <div className="si-otp-group">
              {authCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={`si-otp-inp${digit ? " si-otp-inp--filled" : ""}`}
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>

            {errAuthCode && (
              <p className="si-err-msg si-err-msg--center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {errAuthCode}
              </p>
            )}

            <button type="submit" className="si-btn" disabled={isLoading}>
              {isLoading ? <span className="si-spinner" /> : (
                <>
                  <span>Verify &amp; Continue</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="si-otp-footer">
            <Recover status={status} data={data} loginData={loginData} setModel2FA={setModel2FA} setData={setData} />
            {model2FA && (
              <TwoFactorAuthentication status={status} data={data} model2FA={model2FA} setModel2FA={setModel2FA} />
            )}
            <button onClick={() => setStatus("Login")} className="si-back-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              Back to login
            </button>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

        .si-outer {
          font-family: 'Plus Jakarta Sans', sans-serif;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        /* ── Card box ── */
        .si-box {
          width: 100%;
          max-width: 440px;
          animation: siIn 0.5s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes siIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Header ── */
        .si-hd { margin-bottom: 32px; }

        .si-hd-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          border-radius: 100px;
          font-size: 11.5px;
          font-weight: 600;
          padding: 5px 12px;
          letter-spacing: 0.2px;
          margin-bottom: 18px;
        }
        .si-hd-badge--green {
          background: #f0fdf4;
          color: #15803d;
          border-color: #bbf7d0;
        }

        .si-title {
          font-size: clamp(22px, 3vw, 28px);
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.5px;
          line-height: 1.2;
          margin-bottom: 8px;
        }
        .si-sub {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
        }

        /* ── Form ── */
        .si-form { display: flex; flex-direction: column; gap: 20px; }

        /* ── Field ── */
        .si-field { display: flex; flex-direction: column; gap: 6px; }
        .si-lbl-row { display: flex; justify-content: space-between; align-items: center; }
        .si-lbl {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        /* ── Input ── */
        .si-inp-wrap { position: relative; display: flex; align-items: center; }
        .si-inp-icon {
          position: absolute; left: 14px;
          color: #94a3b8;
          display: flex; align-items: center; pointer-events: none;
        }
        .si-inp {
          width: 100%;
          padding: 13px 44px 13px 42px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          -webkit-appearance: none;
        }
        .si-inp::placeholder { color: #cbd5e1; }
        .si-inp:hover { border-color: #cbd5e1; background: #f1f5f9; }
        .si-inp:focus {
          border-color: #2563eb;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .si-inp--err {
          border-color: #ef4444 !important;
          background: #fff7f7 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.08) !important;
        }

        .si-eye-btn {
          position: absolute; right: 14px;
          background: none; border: none; cursor: pointer;
          color: #94a3b8; display: flex; align-items: center; padding: 4px;
          transition: color 0.18s;
          border-radius: 6px;
        }
        .si-eye-btn:hover { color: #475569; }

        /* ── Error ── */
        .si-err-msg {
          display: flex; align-items: center; gap: 5px;
          font-size: 12px; color: #dc2626; font-weight: 500;
        }
        .si-err-msg--center { justify-content: center; }

        /* ── Submit button ── */
        .si-btn {
          width: 100%;
          padding: 14px 24px;
          background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
          border: none; border-radius: 12px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px; font-weight: 700; color: #fff;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: transform 0.15s, box-shadow 0.18s, opacity 0.18s;
          box-shadow: 0 4px 14px rgba(37,99,235,0.3), 0 1px 3px rgba(37,99,235,0.2);
          position: relative; overflow: hidden;
          -webkit-tap-highlight-color: transparent;
          letter-spacing: 0.1px;
        }
        .si-btn::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent 60%);
        }
        .si-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(37,99,235,0.38), 0 2px 6px rgba(37,99,235,0.2);
        }
        .si-btn:active { transform: translateY(0); }
        .si-btn:disabled { opacity: 0.65; cursor: not-allowed; transform: none; }

        /* ── Spinner ── */
        .si-spinner {
          display: inline-block; width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff; border-radius: 50%;
          animation: siSpin 0.65s linear infinite;
        }
        @keyframes siSpin { to { transform: rotate(360deg); } }

        /* ── Security note ── */
        .si-security-note {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          margin-top: 20px;
          font-size: 12px; color: #94a3b8; font-weight: 500;
        }

        /* ── Divider ── */
        .si-divider {
          display: flex; align-items: center; gap: 12px;
          font-size: 12px; color: #cbd5e1; font-weight: 500;
        }
        .si-divider::before, .si-divider::after {
          content: ''; flex: 1; height: 1px; background: #f1f5f9;
        }

        /* ── OTP ── */
        .si-otp-group {
          display: flex; justify-content: center; gap: 10px;
        }
        .si-otp-inp {
          width: 54px; height: 62px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 24px; font-weight: 700;
          color: #0f172a; text-align: center; outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          -webkit-appearance: none; caret-color: #2563eb;
        }
        .si-otp-inp:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .si-otp-inp--filled {
          border-color: #93c5fd;
          background: #eff6ff;
          color: #1d4ed8;
        }

        /* ── OTP footer ── */
        .si-otp-footer {
          margin-top: 24px;
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 12px;
          padding-top: 20px;
          border-top: 1px solid #f1f5f9;
        }
        .si-back-btn {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600;
          color: #64748b; background: none; border: none; cursor: pointer; padding: 0;
          transition: color 0.18s;
        }
        .si-back-btn:hover { color: #0f172a; }

        /* ── Responsive ── */
        @media (max-width: 960px) {
          .si-box { max-width: 480px; }
        }

        @media (max-width: 600px) {
          .si-outer { align-items: flex-start; }
          .si-box { max-width: 100%; }
          .si-title { font-size: 22px; }
          .si-hd { margin-bottom: 26px; }
          .si-inp { font-size: 16px; padding: 14px 44px 14px 42px; }
          .si-btn { padding: 15px 20px; font-size: 15px; }
          .si-otp-group { gap: 8px; }
          .si-otp-inp { width: 46px; height: 54px; font-size: 20px; border-radius: 12px; }
          .si-otp-footer { flex-direction: column-reverse; align-items: center; text-align: center; }
        }

        @media (max-width: 380px) {
          .si-otp-inp { width: 40px; height: 48px; font-size: 18px; border-radius: 10px; }
          .si-otp-group { gap: 6px; }
        }
      `}</style>
    </div>
  );
}